import { calculateDiff, inventoryTierFromSupply } from "./localHelpers";
import { CATEGORY_IDS } from "./categoryPriority";

/**
 * Area metrics for SentenceTrendsOld — always **6‑Months Trending** vs **Past 6‑Months Trending**
 * and **Same 6 Months Trends Percentage** (all-property `marketTrends` / `marketTrendsPercentage`,
 * no SFR/condo split, no 12‑month fallback).
 */
export function extractAreaMetrics(aprilData, currentArea, agentData, hoData) {
  const { listgenUserID } = agentData;
  const { mlsNeighborhood, mlsCityName, mlsAreaID } = currentArea;
  const { subdivisionName } = hoData || {};

  const { pmaQuarterlyAreaDTO: pmaAprilRunAreaDTO } = aprilData;

  const useMedian = listgenUserID === "102548";

  const area6Months = pmaAprilRunAreaDTO.marketTrends.find(
    (x) => x.TrendName === "6-Months Trending",
  );
  const areaPast6Months = pmaAprilRunAreaDTO.marketTrends.find(
    (x) => x.TrendName === "Past 6-Months Trending",
  );

  const timeline = "6 months";

  const percentageRows = pmaAprilRunAreaDTO.marketTrendsPercentage || [];

  const findPercentageByName = (names) => {
    const list = Array.isArray(names) ? names : [names];
    return (
      percentageRows.find((row) => list.includes(row.TrendPercentageName)) || {}
    );
  };

  const pctSame6MonthsLastYear = findPercentageByName(
    "Same 6 Months Trends Percentage",
  );
  const pct6MonthsTrends = findPercentageByName("6 Months Trends Percentage");

  const areaPercentage = findPercentageByName([
    "Same 6 Months Trends Percentage",
    "6 Months Trends Percentage",
  ]);

  // Per-section percentage row: always same‑6‑months‑vs‑last‑year row first.
  const sectionTimeframeMode = {
    avgSalePrice: "same6",
    avgDom: "same6",
    percReceived: "same6",
    percOverList: "same6",
    avgSqFtPrice: "same6",
    unitsSold: "same6",
    inventory: "same6",
  };

  const getSectionPercentageRow = (sectionKey) => {
    const mode = sectionTimeframeMode[sectionKey];
    if (mode === "six") {
      return findPercentageByName([
        "6 Months Trends Percentage",
        "Same 6 Months Trends Percentage",
      ]);
    }
    return findPercentageByName([
      "Same 6 Months Trends Percentage",
      "6 Months Trends Percentage",
    ]);
  };

  const sectionRows = {
    avgSalePrice: getSectionPercentageRow("avgSalePrice"),
    avgDom: getSectionPercentageRow("avgDom"),
    percReceived: getSectionPercentageRow("percReceived"),
    percOverList: getSectionPercentageRow("percOverList"),
    avgSqFtPrice: getSectionPercentageRow("avgSqFtPrice"),
    inventory: getSectionPercentageRow("inventory"),
  };

  const getSectionPercentageMetric = (sectionKey, key, fallbackValue) => {
    const row = sectionRows[sectionKey] || {};
    const raw = row[key];
    if (raw === undefined || raw === null || raw === "") return +fallbackValue;
    return +raw;
  };

  const sectionTimelineLabel = (sectionKey) => {
    const mode = sectionTimeframeMode[sectionKey];
    if (mode === "same6") return "same 6 months last year";
    return "6 months";
  };

  const sectionComparisonPhrase = (sectionKey) => {
    const mode = sectionTimeframeMode[sectionKey];
    if (mode === "same6") return "the same 6-month period last year";
    return "the previous 6-month period";
  };

  const sectionPeriodPhrase = (sectionKey) => {
    const mode = sectionTimeframeMode[sectionKey];
    if (mode === "same6") return "the same 6-month period last year";
    return "the past 6 months";
  };

  const getPercentageMetric = (key, fallbackValue) => {
    const raw = areaPercentage[key];
    if (raw === undefined || raw === null || raw === "") return +fallbackValue;
    return +raw;
  };

  const {
    AvgDaysOnMarket: area6DOM,
    AvgPercentOfReceiving: area6PercOfReceiving,
    AvgPricePerSqFt: area6SqftPrice,
    AvgSalePrice: area6SalePrice,
    MedianSalePrice: area6MedianSalePrice,
    HomesSold: area6HomesSold,
    Inventory: area6Inventory,
    PercentageSoldAtOrOverListPrice: area6OverListPrice,
  } = area6Months;

  const {
    AvgDaysOnMarket: areaPast6DOM,
    AvgPercentOfReceiving: areaPast6PercOfReceiving,
    AvgPricePerSqFt: areaPast6SqftPrice,
    AvgSalePrice: areaPast6SalePrice,
    MedianSalePrice: areaPast6MedianSalePrice,
    HomesSold: areaPast6HomesSold,
    Inventory: areaPast6Inventory,
    PercentageSoldAtOrOverListPrice: areaPast6OverListPrice,
  } = areaPast6Months;

  let avgSalePrice;
  let avgSalePricePerc;
  let yoyAvgSalePricePerc;
  if (useMedian) {
    yoyAvgSalePricePerc = getPercentageMetric(
      "PercentageMedianSalePricee",
      calculateDiff(area6MedianSalePrice, areaPast6MedianSalePrice),
    );
    avgSalePrice = +area6MedianSalePrice;
    avgSalePricePerc = getSectionPercentageMetric(
      "avgSalePrice",
      "PercentageMedianSalePricee",
      calculateDiff(area6MedianSalePrice, areaPast6MedianSalePrice),
    );
  } else {
    yoyAvgSalePricePerc = getPercentageMetric(
      "PercentageAvgSalePrice",
      calculateDiff(area6SalePrice, areaPast6SalePrice),
    );
    avgSalePrice = +area6SalePrice;
    avgSalePricePerc = getSectionPercentageMetric(
      "avgSalePrice",
      "PercentageAvgSalePrice",
      calculateDiff(area6SalePrice, areaPast6SalePrice),
    );
  }

  const avgDom = +area6DOM;
  const pastAvgDom = +areaPast6DOM;
  const avgDomPerc = getSectionPercentageMetric(
    "avgDom",
    "PercentageAvgDaysOnMarket",
    calculateDiff(area6DOM, areaPast6DOM),
  );

  const percReceived = +area6PercOfReceiving;
  const percReceivedPerc = getSectionPercentageMetric(
    "percReceived",
    "PercentageAvgPercentOfReceiving",
    calculateDiff(area6PercOfReceiving, areaPast6PercOfReceiving),
  );

  const percOverList = +area6OverListPrice;
  const percOverListPerc = getSectionPercentageMetric(
    "percOverList",
    "PercentageSoldAtOrOverListPrice",
    calculateDiff(area6OverListPrice, areaPast6OverListPrice),
  );

  const avgSqFtPrice = +area6SqftPrice;
  const pastAvgSqFtPrice = +areaPast6SqftPrice;
  const avgSqFtPricePerc = getSectionPercentageMetric(
    "avgSqFtPrice",
    "PercentageAvgPricePerSqFt",
    calculateDiff(area6SqftPrice, areaPast6SqftPrice),
  );

  const unitsSold = +area6HomesSold;
  const pastUnitsSold = +areaPast6HomesSold;
  const unitsSoldPerc = +calculateDiff(area6HomesSold, areaPast6HomesSold);

  const inventory = +area6Inventory;
  const inventoryPerc = getSectionPercentageMetric(
    "inventory",
    "PercentageInventory",
    calculateDiff(area6Inventory, areaPast6Inventory),
  );

  const totalActiveListings = +pmaAprilRunAreaDTO.totalActiveListings;

  const { monthsSupplyTable } = pmaAprilRunAreaDTO;
  const { AreaNumberOfHousesForSale, AvgHomesSoldLast12Months } =
    monthsSupplyTable;

  const inventoryTier = inventoryTierFromSupply(
    AreaNumberOfHousesForSale,
    Number(AvgHomesSoldLast12Months) === 0
      ? 1
      : Number(AvgHomesSoldLast12Months),
  );

  let rawPlace;
  if (subdivisionName) {
    rawPlace = subdivisionName;
  } else if (mlsAreaID === "SBA-20FInv" || mlsAreaID === "SBA-20FRes") {
    rawPlace = "Downtown";
  } else {
    rawPlace = mlsNeighborhood;
  }

  const periodPhrase = "the past 6 months";
  const comparisonPhrase = "the same 6-month period last year";

  const pickRowPct = (row, key, fallback) => {
    if (!row || Object.keys(row).length === 0) return +fallback;
    const raw = row[key];
    if (raw === undefined || raw === null || raw === "") return +fallback;
    return +raw;
  };

  /**
   * Full trend slice + YoY % row for the rolling 6‑month vs prior 6‑month pair,
   * with % fields from the Same 6 Months Trends Percentage row.
   */
  const bundleComparisonWindow = (cur, past, pctRow, comparisonPhrase) => {
    const c = cur || {};
    const p = past || {};
    const saleCur = useMedian ? c.MedianSalePrice : c.AvgSalePrice;
    const salePast = useMedian ? p.MedianSalePrice : p.AvgSalePrice;
    const salePctKey = useMedian
      ? "PercentageMedianSalePricee"
      : "PercentageAvgSalePrice";

    return {
      comparisonPhrase,
      avgSalePrice: +saleCur,
      pastAvgSalePrice: +salePast,
      avgSalePricePerc: pickRowPct(
        pctRow,
        salePctKey,
        calculateDiff(saleCur, salePast),
      ),
      avgSqFtPrice: +c.AvgPricePerSqFt,
      pastAvgSqFtPrice: +p.AvgPricePerSqFt,
      avgSqFtPricePerc: pickRowPct(
        pctRow,
        "PercentageAvgPricePerSqFt",
        calculateDiff(c.AvgPricePerSqFt, p.AvgPricePerSqFt),
      ),
      avgDom: +c.AvgDaysOnMarket,
      pastAvgDom: +p.AvgDaysOnMarket,
      avgDomPerc: pickRowPct(
        pctRow,
        "PercentageAvgDaysOnMarket",
        calculateDiff(c.AvgDaysOnMarket, p.AvgDaysOnMarket),
      ),
      percReceived: +c.AvgPercentOfReceiving,
      pastPercReceived: +p.AvgPercentOfReceiving,
      percReceivedPerc: pickRowPct(
        pctRow,
        "PercentageAvgPercentOfReceiving",
        calculateDiff(c.AvgPercentOfReceiving, p.AvgPercentOfReceiving),
      ),
      percOverList: +c.PercentageSoldAtOrOverListPrice,
      pastPercOverList: +p.PercentageSoldAtOrOverListPrice,
      percOverListPerc: pickRowPct(
        pctRow,
        "PercentageSoldAtOrOverListPrice",
        calculateDiff(
          c.PercentageSoldAtOrOverListPrice,
          p.PercentageSoldAtOrOverListPrice,
        ),
      ),
      unitsSold: +c.HomesSold,
      pastUnitsSold: +p.HomesSold,
      unitsSoldPerc: pickRowPct(
        pctRow,
        "PercentageNumberOfHomesSold",
        calculateDiff(c.HomesSold, p.HomesSold),
      ),
      inventory: +c.Inventory,
      pastInventory: +p.Inventory,
      inventoryPerc: pickRowPct(
        pctRow,
        "PercentageInventory",
        calculateDiff(c.Inventory, p.Inventory),
      ),
    };
  };

  const windowSame6LastYear = bundleComparisonWindow(
    area6Months,
    areaPast6Months,
    pctSame6MonthsLastYear,
    "the same 6-month period last year",
  );

  /** Per category: same 6‑month vs same period last year (YoY % from Same 6 Months row). */
  const sentenceTrendsOldCategoryWindows = {
    [CATEGORY_IDS.AVERAGE_HOME_PRICE]: {
      same6LastYear: {
        comparisonPhrase: windowSame6LastYear.comparisonPhrase,
        avgSalePrice: windowSame6LastYear.avgSalePrice,
        pastAvgSalePrice: windowSame6LastYear.pastAvgSalePrice,
        avgSalePricePerc: windowSame6LastYear.avgSalePricePerc,
      },
    },
    [CATEGORY_IDS.PRICE_PER_SQFT]: {
      same6LastYear: {
        comparisonPhrase: windowSame6LastYear.comparisonPhrase,
        avgSqFtPrice: windowSame6LastYear.avgSqFtPrice,
        pastAvgSqFtPrice: windowSame6LastYear.pastAvgSqFtPrice,
        avgSqFtPricePerc: windowSame6LastYear.avgSqFtPricePerc,
      },
    },
    [CATEGORY_IDS.PCT_RECEIVED]: {
      same6LastYear: {
        comparisonPhrase: windowSame6LastYear.comparisonPhrase,
        percReceived: windowSame6LastYear.percReceived,
        pastPercReceived: windowSame6LastYear.pastPercReceived,
        percReceivedPerc: windowSame6LastYear.percReceivedPerc,
      },
    },
    [CATEGORY_IDS.AVG_DOM]: {
      same6LastYear: {
        comparisonPhrase: windowSame6LastYear.comparisonPhrase,
        avgDom: windowSame6LastYear.avgDom,
        pastAvgDom: windowSame6LastYear.pastAvgDom,
        avgDomPerc: windowSame6LastYear.avgDomPerc,
      },
    },
    [CATEGORY_IDS.UNITS_SOLD]: {
      same6LastYear: {
        comparisonPhrase: windowSame6LastYear.comparisonPhrase,
        unitsSold: windowSame6LastYear.unitsSold,
        pastUnitsSold: windowSame6LastYear.pastUnitsSold,
        unitsSoldPerc: windowSame6LastYear.unitsSoldPerc,
      },
    },
    [CATEGORY_IDS.INVENTORY]: {
      same6LastYear: {
        comparisonPhrase: windowSame6LastYear.comparisonPhrase,
        inventory: windowSame6LastYear.inventory,
        pastInventory: windowSame6LastYear.pastInventory,
        inventoryPerc: windowSame6LastYear.inventoryPerc,
      },
    },
  };

  return {
    timeline,
    useMedian,
    mlsAreaID,
    mlsCityName: mlsAreaID === "ALA-2811D" ? "Piedmont" : mlsCityName,
    rawPlace,
    periodPhrase,
    comparisonPhrase,
    phrasesBySection: {
      avgSalePrice: {
        periodPhrase: sectionPeriodPhrase("avgSalePrice"),
        comparisonPhrase: sectionComparisonPhrase("avgSalePrice"),
        timeline: sectionTimelineLabel("avgSalePrice"),
      },
      avgSqFtPrice: {
        periodPhrase: sectionPeriodPhrase("avgSqFtPrice"),
        comparisonPhrase: sectionComparisonPhrase("avgSqFtPrice"),
        timeline: sectionTimelineLabel("avgSqFtPrice"),
      },
      percReceived: {
        periodPhrase: sectionPeriodPhrase("percReceived"),
        comparisonPhrase: sectionComparisonPhrase("percReceived"),
        timeline: sectionTimelineLabel("percReceived"),
      },
      avgDom: {
        periodPhrase: sectionPeriodPhrase("avgDom"),
        comparisonPhrase: sectionComparisonPhrase("avgDom"),
        timeline: sectionTimelineLabel("avgDom"),
      },
      unitsSold: {
        periodPhrase: sectionPeriodPhrase("unitsSold"),
        comparisonPhrase: sectionComparisonPhrase("unitsSold"),
        timeline: sectionTimelineLabel("unitsSold"),
      },
      inventory: {
        periodPhrase: sectionPeriodPhrase("inventory"),
        comparisonPhrase: sectionComparisonPhrase("inventory"),
        timeline: sectionTimelineLabel("inventory"),
      },
    },
    avgSalePrice,
    avgSalePricePerc: +avgSalePricePerc,
    yoyAvgSalePricePerc: +yoyAvgSalePricePerc,
    avgDom,
    pastAvgDom,
    avgDomPerc,
    percReceived,
    percReceivedPerc,
    percOverList,
    percOverListPerc,
    avgSqFtPrice,
    pastAvgSqFtPrice,
    avgSqFtPricePerc,
    unitsSold,
    pastUnitsSold,
    unitsSoldPerc,
    inventory,
    inventoryPerc,
    totalActiveListings,
    areaInventoryHomesForSale: AreaNumberOfHousesForSale,
    avgHomesSoldLast12Months: AvgHomesSoldLast12Months,
    inventoryTier,
    sentenceTrendsOldCategoryWindows,
  };
}
